import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ProviderType, BookingAccessStatus } from "@/types/booking";

export function useUniversalBookingAccess(
  providerType: ProviderType | undefined,
  providerId: string | undefined
): BookingAccessStatus {
  const [access, setAccess] = useState<BookingAccessStatus>({
    hasAccess: false,
    status: 'loading',
    daysRemaining: null,
    trialEndsAt: null,
    trialStartedAt: null,
    subscriptionTier: null,
    isLoading: true,
  });

  useEffect(() => {
    if (!providerType || !providerId) {
      setAccess(prev => ({ ...prev, status: 'none', isLoading: false }));
      return;
    }

    const checkAccess = async () => {
      try {
        const { data, error } = await supabase
          .from("universal_booking_subscriptions")
          .select("*")
          .eq("provider_type", providerType)
          .eq("provider_id", providerId)
          .maybeSingle();

        if (error) {
          console.error("Error checking universal booking access:", error);
          setAccess({
            hasAccess: false,
            status: 'none',
            daysRemaining: null,
            trialEndsAt: null,
            trialStartedAt: null,
            subscriptionTier: null,
            isLoading: false,
          });
          return;
        }

        if (!data) {
          setAccess({
            hasAccess: false,
            status: 'none',
            daysRemaining: null,
            trialEndsAt: null,
            trialStartedAt: null,
            subscriptionTier: null,
            isLoading: false,
          });
          return;
        }

        const trialEndsAt = new Date(data.trial_ends_at);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // Access is granted if:
        // 1. Trial is active (status=trial && days remaining > 0)
        // 2. Subscription is active (status=active)
        // 3. Subscription tier is annual or lifetime
        const hasAccess =
          (data.subscription_status === 'trial' && daysRemaining > 0) ||
          data.subscription_status === 'active' ||
          data.subscription_tier === 'annual' ||
          data.subscription_tier === 'lifetime';

        // Determine actual status
        let status: BookingAccessStatus['status'] = data.subscription_status as any;
        if (data.subscription_status === 'trial' && daysRemaining <= 0) {
          status = 'expired';
        }

        setAccess({
          hasAccess,
          status,
          daysRemaining: Math.max(0, daysRemaining),
          trialEndsAt: data.trial_ends_at,
          trialStartedAt: data.trial_started_at,
          subscriptionTier: data.subscription_tier as 'annual' | 'lifetime' | null,
          isLoading: false,
        });
      } catch (err) {
        console.error("Error in useUniversalBookingAccess:", err);
        setAccess(prev => ({ ...prev, status: 'none', isLoading: false }));
      }
    };

    checkAccess();
  }, [providerType, providerId]);

  return access;
}

// Helper to start a trial for a provider
export async function startUniversalBookingTrial(
  providerType: ProviderType,
  providerId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if subscription already exists
    const { data: existing } = await supabase
      .from("universal_booking_subscriptions")
      .select("id")
      .eq("provider_type", providerType)
      .eq("provider_id", providerId)
      .maybeSingle();

    if (existing) {
      return { success: true }; // Already has subscription
    }

    const { error } = await supabase
      .from("universal_booking_subscriptions")
      .insert({
        provider_type: providerType,
        provider_id: providerId,
        user_id: userId,
        subscription_status: 'trial',
        trial_started_at: new Date().toISOString(),
        trial_ends_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      });

    if (error) {
      console.error("Error starting booking trial:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error("Error in startUniversalBookingTrial:", err);
    return { success: false, error: "Error inesperado" };
  }
}
